import { useEffect, useState, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  LogOut,
  Calendar,
  UtensilsCrossed,
  Mic2,
  Plus,
  Pencil,
  Trash2,
  Upload,
  Loader2,
  X,
  Save,
  CalendarClock,
  Soup,
  ListOrdered,
  Sparkles,
  LayoutGrid,
  Settings as SettingsIcon,
} from "lucide-react";
import api, { apiErrorMessage, imageUrl } from "@/lib/api";
import {
  WeeklyEventsManager,
  DishesManager,
  MenuItemsManager,
  MenuCategoriesManager,
  SpecialsManager,
  SettingsManager,
} from "@/pages/AdminExtras";

const TABS = [
  { id: "events", label: "Events", icon: Calendar },
  { id: "weekly", label: "Weekly Schedule", icon: CalendarClock },
  { id: "categories", label: "Menu Categories", icon: LayoutGrid },
  { id: "menu_items", label: "Menu Items", icon: ListOrdered },
  { id: "menu", label: "Menu Photos", icon: UtensilsCrossed },
  { id: "dishes", label: "Featured Dishes", icon: Soup },
  { id: "specials", label: "Specials", icon: Sparkles },
  { id: "performers", label: "Performers", icon: Mic2 },
  { id: "settings", label: "Site Settings", icon: SettingsIcon },
];

// Menu category options are now loaded dynamically from the backend so the
// MenuPhotos editor stays in sync with the Menu Categories tab.
function useMenuCategoryOptions() {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    api
      .get("/api/menu-categories")
      .then((res) => {
        const arr = Array.isArray(res.data) ? res.data : [];
        setOpts(arr.map((c) => ({ value: c.slug, label: c.title })));
      })
      .catch(() => setOpts([]));
  }, []);
  return opts;
}

export default function Admin() {
  const [tab, setTab] = useState("events");
  const navigate = useNavigate();

  if (!localStorage.getItem("tnt_admin_token")) {
    return <Navigate to="/admin/login" replace />;
  }

  const logout = () => {
    localStorage.removeItem("tnt_admin_token");
    localStorage.removeItem("tnt_admin_email");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-tnt-bg pt-4 pb-16 px-4 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-6xl">
        <header className="flex items-center justify-between mb-6">
          <div>
            <p className="font-display text-tnt-gold text-[10px] sm:text-xs tracking-[0.4em] uppercase mb-1">
              Taps &amp; Tunes
            </p>
            <h1 className="font-display text-2xl sm:text-4xl text-tnt-ink tracking-tight">
              ADMIN <span className="gold-text">PANEL</span>
            </h1>
          </div>
          <button
            type="button"
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 px-3 py-2 border border-tnt-border text-tnt-ink/80 hover:text-tnt-ink hover:border-tnt-ink/40 rounded-md text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </header>

        <nav className="flex gap-1 border-b border-tnt-border mb-7 -mx-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`shrink-0 inline-flex items-center gap-2 px-4 py-3 font-display tracking-widest uppercase text-xs transition-colors border-b-2 -mb-px ${
                  active
                    ? "border-tnt-gold text-tnt-ink"
                    : "border-transparent text-tnt-ink/55 hover:text-tnt-ink"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>

        {tab === "events" && <EventsManager />}
        {tab === "weekly" && <WeeklyEventsManager />}
        {tab === "categories" && <MenuCategoriesManager />}
        {tab === "menu_items" && <MenuItemsManager />}
        {tab === "menu" && <MenuPhotosManager />}
        {tab === "dishes" && <DishesManager />}
        {tab === "specials" && <SpecialsManager />}
        {tab === "performers" && <PerformersManager />}
        {tab === "settings" && <SettingsManager />}
      </div>
    </div>
  );
}

// ============== EVENTS ==============
function EventsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/events");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    try {
      await api.delete(`/api/admin/events/${id}`);
      setItems((arr) => arr.filter((e) => e.id !== id));
    } catch (err) {
      alert(apiErrorMessage(err));
    }
  };

  return (
    <section data-testid="admin-events" className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl text-tnt-ink tracking-tight">
          Events ({items.length})
        </h2>
        <button
          type="button"
          data-testid="admin-event-add"
          onClick={() =>
            setEditing({
              id: null,
              title: "",
              date: "",
              description: "",
              image_url: "",
              order: 0,
            })
          }
          className="btn-gold !py-2 !px-4 !text-sm"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New event
        </button>
      </div>

      <p className="text-tnt-ink/55 text-xs">
        Use the <strong>Display order</strong> field on each event to reorder them on the
        public Events page (lower numbers show first).
      </p>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Calendar className="w-6 h-6" />}
          title="No events yet"
          subtitle="Click 'New event' to add your first one."
        />
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((e) => (
            <li
              key={e.id}
              data-testid={`admin-event-${e.id}`}
              className="bg-white border border-tnt-border rounded-lg overflow-hidden shadow-sm"
            >
              {e.image_url ? (
                <img
                  src={imageUrl(e.image_url)}
                  alt={e.title}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover bg-tnt-surface2"
                />
              ) : (
                <div className="w-full aspect-[4/3] flex items-center justify-center bg-tnt-surface2 text-tnt-ink/40 text-xs">
                  No photo
                </div>
              )}
              <div className="p-4">
                <p className="font-display text-tnt-gold text-[10px] tracking-[0.3em] uppercase">
                  {e.date || "—"}
                </p>
                <h3 className="font-display text-lg text-tnt-ink mt-1 leading-tight">
                  {e.title}
                </h3>
                <p className="text-tnt-ink/50 text-[11px] mt-1">
                  Order: {e.order}
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    data-testid={`admin-event-edit-${e.id}`}
                    onClick={() => setEditing(e)}
                    className="inline-flex items-center gap-1.5 text-tnt-ink hover:text-tnt-gold text-xs font-display tracking-widest uppercase"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-event-delete-${e.id}`}
                    onClick={() => remove(e.id)}
                    className="ml-auto inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 text-xs font-display tracking-widest uppercase"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <EventEditor
          event={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function EventEditor({ event, onClose, onSaved }) {
  const [title, setTitle] = useState(event.title || "");
  const [date, setDate] = useState(event.date || "");
  const [description, setDescription] = useState(event.description || "");
  const [order, setOrder] = useState(event.order ?? 0);
  const [imageUrlState, setImageUrlState] = useState(event.image_url || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/api/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrlState(data.image_url);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        title,
        date,
        description,
        image_url: imageUrlState,
        order: Number(order) || 0,
      };
      if (event.id) {
        await api.put(`/api/admin/events/${event.id}`, payload);
      } else {
        await api.post("/api/admin/events", payload);
      }
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={event.id ? "Edit event" : "New event"} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Title">
          <input
            type="text"
            data-testid="event-form-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="form-input"
          />
        </Field>
        <Field label="Date label" hint="e.g. 'Friday, March 14' or 'Every Tuesday'">
          <input
            type="text"
            data-testid="event-form-date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="form-input"
          />
        </Field>
        <Field label="Description (optional)">
          <textarea
            data-testid="event-form-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="form-input resize-none"
          />
        </Field>
        <Field label="Display order" hint="Lower numbers appear first on the website.">
          <input
            type="number"
            data-testid="event-form-order"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            className="form-input w-32"
          />
        </Field>

        <Field label="Poster / photo">
          {imageUrlState ? (
            <div className="space-y-2">
              <img
                src={imageUrl(imageUrlState)}
                alt=""
                className="w-full max-h-72 object-contain rounded bg-tnt-surface2"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="text-xs font-display tracking-widest uppercase text-tnt-ink hover:text-tnt-gold"
                >
                  Replace
                </button>
                <button
                  type="button"
                  onClick={() => setImageUrlState("")}
                  className="text-xs font-display tracking-widest uppercase text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              data-testid="event-form-upload"
              onClick={() => fileRef.current?.click()}
              className="w-full border border-dashed border-tnt-border bg-white hover:bg-tnt-surface rounded-md py-8 text-tnt-ink/65 hover:text-tnt-ink transition-colors inline-flex flex-col items-center justify-center gap-2"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span className="text-sm">{uploading ? "Uploading…" : "Choose photo"}</span>
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPickFile}
          />
        </Field>

        {error && <FormError>{error}</FormError>}
        <FormActions onCancel={onClose} saving={saving} />
      </form>
    </Modal>
  );
}

// ============== MENU PHOTOS (with categories + edit) ==============
function MenuPhotosManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const categoryOptions = useMenuCategoryOptions();
  const labelFor = (slug) =>
    categoryOptions.find((c) => c.value === slug)?.label || slug;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/menu-photos");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this menu photo?")) return;
    try {
      await api.delete(`/api/admin/menu-photos/${id}`);
      setItems((arr) => arr.filter((e) => e.id !== id));
    } catch (err) {
      alert(apiErrorMessage(err));
    }
  };

  return (
    <section data-testid="admin-menu" className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl text-tnt-ink tracking-tight">
          Menu Photos ({items.length})
        </h2>
        <button
          type="button"
          data-testid="admin-menu-add"
          onClick={() =>
            setEditing({
              id: null,
              name: "",
              description: "",
              category: categoryOptions[0]?.value || "",
              order: 0,
              image_url: "",
              file: null,
            })
          }
          className="btn-gold !py-2 !px-4 !text-sm"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add photo
        </button>
      </div>
      <p className="text-tnt-ink/55 text-xs">
        Each photo's <strong>category</strong> decides which section of the public Menu
        page it appears under.
      </p>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<UtensilsCrossed className="w-6 h-6" />}
          title="No menu photos yet"
          subtitle="Add photos of dishes — each one will appear in its category."
        />
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m) => (
            <li
              key={m.id}
              data-testid={`admin-menu-${m.id}`}
              className="bg-white border border-tnt-border rounded-lg overflow-hidden shadow-sm"
            >
              <img
                src={imageUrl(m.image_url)}
                alt={m.name}
                loading="lazy"
                className="w-full aspect-square object-cover bg-tnt-surface2"
              />
              <div className="p-3">
                <p className="font-display text-tnt-gold text-[10px] tracking-[0.28em] uppercase mb-0.5">
                  {labelFor(m.category)}
                </p>
                <h3 className="font-display text-tnt-ink leading-tight text-base truncate">
                  {m.name}
                </h3>
                {m.description && (
                  <p className="text-tnt-ink/60 text-xs mt-1 line-clamp-2">
                    {m.description}
                  </p>
                )}
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    data-testid={`admin-menu-edit-${m.id}`}
                    onClick={() => setEditing({ ...m, file: null })}
                    className="inline-flex items-center gap-1 text-tnt-ink hover:text-tnt-gold text-[11px] font-display tracking-widest uppercase"
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-menu-delete-${m.id}`}
                    onClick={() => remove(m.id)}
                    className="ml-auto inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-[11px] font-display tracking-widest uppercase"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <MenuPhotoEditor
          item={editing}
          categoryOptions={categoryOptions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function MenuPhotoEditor({ item, categoryOptions, onClose, onSaved }) {
  const [name, setName] = useState(item.name || "");
  const [description, setDescription] = useState(item.description || "");
  const [category, setCategory] = useState(item.category || categoryOptions[0]?.value || "");
  const [order, setOrder] = useState(item.order ?? 0);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = Boolean(item.id);

  const submit = async (e) => {
    e.preventDefault();
    if (!isEdit && !file) {
      setError("Please choose a photo.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await api.put(`/api/admin/menu-photos/${item.id}`, {
          name,
          description,
          category,
          order: Number(order) || 0,
        });
      } else {
        const form = new FormData();
        form.append("name", name);
        form.append("description", description);
        form.append("category", category);
        form.append("order", String(Number(order) || 0));
        form.append("file", file);
        await api.post("/api/admin/menu-photos", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? "Edit menu photo" : "Add menu photo"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Dish name">
          <input
            type="text"
            data-testid="menu-form-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="form-input"
          />
        </Field>
        <Field label="Description (optional)">
          <input
            type="text"
            data-testid="menu-form-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="form-input"
          />
        </Field>
        <Field label="Category" hint="Where this photo appears on the public Menu page.">
          <select
            data-testid="menu-form-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="form-input"
          >
            {categoryOptions.length === 0 && <option value="">No categories yet — create one first</option>}
            {categoryOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Display order">
          <input
            type="number"
            data-testid="menu-form-order"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            className="form-input w-32"
          />
        </Field>
        {!isEdit && (
          <Field label="Photo">
            <input
              type="file"
              accept="image/*"
              data-testid="menu-form-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
              className="block w-full text-sm text-tnt-ink/80 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-tnt-gold file:text-black file:font-display file:tracking-widest file:text-xs file:uppercase hover:file:bg-tnt-gold/90 cursor-pointer"
            />
          </Field>
        )}
        {isEdit && item.image_url && (
          <Field label="Photo">
            <img
              src={imageUrl(item.image_url)}
              alt={name}
              className="w-32 aspect-square object-cover rounded border border-tnt-border"
            />
            <p className="text-xs text-tnt-ink/50 mt-2">
              To change the photo, delete and re-upload.
            </p>
          </Field>
        )}
        {error && <FormError>{error}</FormError>}
        <FormActions onCancel={onClose} saving={saving} />
      </form>
    </Modal>
  );
}

// ============== FEATURED PERFORMERS ==============
function PerformersManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/performers");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this performer?")) return;
    try {
      await api.delete(`/api/admin/performers/${id}`);
      setItems((arr) => arr.filter((e) => e.id !== id));
    } catch (err) {
      alert(apiErrorMessage(err));
    }
  };

  return (
    <section data-testid="admin-performers" className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl text-tnt-ink tracking-tight">
          Featured Performers ({items.length})
        </h2>
        <button
          type="button"
          data-testid="admin-performer-add"
          onClick={() =>
            setEditing({
              id: null,
              name: "",
              type: "Comedian",
              order: 0,
              image_url: "",
              file: null,
            })
          }
          className="btn-gold !py-2 !px-4 !text-sm"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add performer
        </button>
      </div>
      <p className="text-tnt-ink/55 text-xs">
        These photos appear on the homepage's <strong>Featured Performers</strong>{" "}
        carousel. Use the Display order field to reorder.
      </p>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Mic2 className="w-6 h-6" />}
          title="No performers yet"
          subtitle="Add photos of comedians, bands, and special guests."
        />
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((p) => (
            <li
              key={p.id}
              data-testid={`admin-performer-${p.id}`}
              className="bg-white border border-tnt-border rounded-lg overflow-hidden shadow-sm"
            >
              <img
                src={imageUrl(p.image_url)}
                alt={p.name}
                loading="lazy"
                className="w-full aspect-square object-cover bg-tnt-surface2"
              />
              <div className="p-3">
                {p.type && (
                  <p className="font-display text-tnt-gold text-[10px] tracking-[0.28em] uppercase mb-0.5">
                    {p.type}
                  </p>
                )}
                <h3 className="font-display text-tnt-ink leading-tight text-base truncate">
                  {p.name}
                </h3>
                <p className="text-tnt-ink/50 text-[11px] mt-0.5">
                  Order: {p.order}
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    data-testid={`admin-performer-edit-${p.id}`}
                    onClick={() => setEditing({ ...p, file: null })}
                    className="inline-flex items-center gap-1 text-tnt-ink hover:text-tnt-gold text-[11px] font-display tracking-widest uppercase"
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-performer-delete-${p.id}`}
                    onClick={() => remove(p.id)}
                    className="ml-auto inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-[11px] font-display tracking-widest uppercase"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <PerformerEditor
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function PerformerEditor({ item, onClose, onSaved }) {
  const [name, setName] = useState(item.name || "");
  const [type, setType] = useState(item.type || "Comedian");
  const [order, setOrder] = useState(item.order ?? 0);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = Boolean(item.id);

  const submit = async (e) => {
    e.preventDefault();
    if (!isEdit && !file) {
      setError("Please choose a photo.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await api.put(`/api/admin/performers/${item.id}`, {
          name,
          type,
          order: Number(order) || 0,
        });
      } else {
        const form = new FormData();
        form.append("name", name);
        form.append("type", type);
        form.append("order", String(Number(order) || 0));
        form.append("file", file);
        await api.post("/api/admin/performers", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? "Edit performer" : "Add performer"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Performer name">
          <input
            type="text"
            data-testid="performer-form-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="form-input"
            placeholder="e.g. Kenny vs. Spenny"
          />
        </Field>
        <Field
          label="Type / category"
          hint="e.g. Comedian, Comedy Duo, Comedy Special, Live Band, Special Event"
        >
          <input
            type="text"
            data-testid="performer-form-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="form-input"
          />
        </Field>
        <Field label="Display order">
          <input
            type="number"
            data-testid="performer-form-order"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            className="form-input w-32"
          />
        </Field>
        {!isEdit && (
          <Field label="Photo">
            <input
              type="file"
              accept="image/*"
              data-testid="performer-form-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
              className="block w-full text-sm text-tnt-ink/80 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-tnt-gold file:text-black file:font-display file:tracking-widest file:text-xs file:uppercase hover:file:bg-tnt-gold/90 cursor-pointer"
            />
          </Field>
        )}
        {isEdit && item.image_url && (
          <Field label="Photo">
            <img
              src={imageUrl(item.image_url)}
              alt={name}
              className="w-32 aspect-square object-cover rounded border border-tnt-border"
            />
            <p className="text-xs text-tnt-ink/50 mt-2">
              To change the photo, delete and re-upload.
            </p>
          </Field>
        )}
        {error && <FormError>{error}</FormError>}
        <FormActions onCancel={onClose} saving={saving} />
      </form>
    </Modal>
  );
}

// ============== Shared bits ==============
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-tnt-border">
          <h3 className="font-display text-tnt-ink text-lg tracking-tight">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="modal-close"
            className="w-8 h-8 inline-flex items-center justify-center rounded-md hover:bg-tnt-surface text-tnt-ink/60 hover:text-tnt-ink"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="font-display tracking-widest text-[11px] uppercase text-tnt-ink/70">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-xs text-tnt-ink/50">{hint}</p>}
    </label>
  );
}

function FormError({ children }) {
  return (
    <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
      {children}
    </p>
  );
}

function FormActions({ onCancel, saving }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onCancel}
        data-testid="form-cancel"
        className="px-4 py-2 border border-tnt-border text-tnt-ink/80 hover:text-tnt-ink rounded-md text-sm"
      >
        Cancel
      </button>
      <button
        type="submit"
        data-testid="form-save"
        disabled={saving}
        className="btn-gold !py-2 !px-4 !text-sm disabled:opacity-60"
      >
        {saving ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <Save className="w-4 h-4 mr-1.5" /> Save
          </>
        )}
      </button>
    </div>
  );
}

function Loading() {
  return (
    <div className="py-16 flex items-center justify-center text-tnt-ink/50">
      <Loader2 className="w-5 h-5 animate-spin" />
    </div>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="py-16 px-6 text-center border border-dashed border-tnt-border rounded-xl bg-white/50">
      <div className="mx-auto w-12 h-12 rounded-full bg-tnt-surface2 inline-flex items-center justify-center text-tnt-ink/50 mb-3">
        {icon}
      </div>
      <h3 className="font-display text-tnt-ink text-lg">{title}</h3>
      <p className="text-tnt-ink/55 text-sm mt-1">{subtitle}</p>
    </div>
  );
}
